import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import HeroSlideshow from '@/components/HeroSlideshow';
import CommitmentSlider, { defaultCoverageContent } from '@/components/CommitmentSlider';
import ActivitiesCarousel, { defaultActivitiesSlides } from '@/components/ActivitiesCarousel';
import MarkdownText from '@/components/MarkdownText';
import { ArrowRight, Users, FileText, Calendar, ChevronRight, Shield, DollarSign, TrendingUp, Globe, BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [latestNews, setLatestNews] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroContent, setHeroContent] = useState({
    title: 'Empowering Public Employees of',
    highlight: 'Papua New Guinea',
    description: 'Join us in our mission to advocate for better working conditions, fair treatment, and professional development opportunities for all public sector employees across PNG.',
    button_primary: 'Become a Member',
    button_secondary: 'Learn More'
  });
  const [dividerContent, setDividerContent] = useState({
    enabled: true,
    height_px: 2,
    color_1: '#f97316',
    color_2: '#60a5fa',
    color_3: '#4ade80',
  });

  const [activitiesContent, setActivitiesContent] = useState({
    interval_ms: 4000,
    slides: defaultActivitiesSlides,
  });

  const [standForContent, setStandForContent] = useState({
    heading: 'What We Stand For',
    subheading:
      'Our mission is to protect, represent, and empower public sector workers across Papua New Guinea.',
    cards: [
      {
        icon: 'Shield',
        title: "Workers' Rights",
        body_markdown:
          'Advocating for fair treatment, job security, and protection against unfair dismissal or discrimination.',
      },
      {
        icon: 'DollarSign',
        title: 'Fair Wages',
        body_markdown:
          'Negotiating competitive salaries and benefits that reflect the value and dedication of public employees.',
      },
      {
        icon: 'TrendingUp',
        title: 'Development',
        body_markdown:
          'Providing training, resources, and opportunities for professional growth and career advancement.',
      },
    ],
  });

  const [coverageContent, setCoverageContent] = useState(defaultCoverageContent);

  const [ctaContent, setCtaContent] = useState({
    heading: 'Ready to Join Our Community?',
    body_markdown:
      'Become part of the largest public employees association in Papua New Guinea and enjoy exclusive benefits.',
    button_text: 'Join Now',
    button_link: '/membership',
  });

  const [worldLabourNews, setWorldLabourNews] = useState({
    heading: 'World Labour News',
    subheading: 'Stay informed about international labour developments and workers\' rights movements',
    categories: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [newsRes, eventsRes, contentRes] = await Promise.all([
        axios.get(`${API}/news`),
        axios.get(`${API}/events`),
        axios.get(`${API}/page-content/home`)
      ]);
      setLatestNews(newsRes.data.slice(0, 3));
      setUpcomingEvents(eventsRes.data.slice(0, 3));
      
      // Set page content
      contentRes.data.forEach(item => {
        if (item.section_name === 'hero') {
          setHeroContent(item.content);
        } else if (item.section_name === 'divider') {
          setDividerContent(item.content);
        } else if (item.section_name === 'activities') {
          setActivitiesContent(item.content);
        } else if (item.section_name === 'stand_for') {
          setStandForContent(item.content);
        } else if (item.section_name === 'coverage') {
          setCoverageContent(item.content);
        } else if (item.section_name === 'cta') {
          setCtaContent(item.content);
        } else if (item.section_name === 'world_labour_news') {
          setWorldLabourNews(item.content);
        }
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const heroMode = heroContent.image_mode || heroContent.mode || 'static';

  const normalizeGoogleDriveImageUrl = (url) => {
    if (!url || typeof url !== 'string') return '';

    // Already direct
    if (url.includes('lh3.googleusercontent.com/d/')) return url;

    // drive.google.com/file/d/<ID>/view
    const m1 = url.match(/drive\.google\.com\/file\/d\/([^/]+)\//);
    if (m1 && m1[1]) return `https://lh3.googleusercontent.com/d/${m1[1]}=s2000`;

    // drive.google.com/open?id=<ID>
    const m2 = url.match(/[?&]id=([^&]+)/);
    if (url.includes('drive.google.com/open') && m2 && m2[1]) {
      return `https://lh3.googleusercontent.com/d/${m2[1]}=s2000`;
    }

    return url;
  };

  // Support both legacy shapes:
  // - image_mode + images[] (existing UI)
  // - mode + background_image (older CMS saves)
  // In static mode we prefer `background_image` if present.
  const heroStaticImage = normalizeGoogleDriveImageUrl(
    heroContent.background_image ||
      (heroContent.images && heroContent.images.length > 0 ? heroContent.images[0] : '')
  );

  const heroSlideshowImages = (
    heroContent.images && heroContent.images.length > 0
      ? heroContent.images
      : heroContent.background_image
        ? [heroContent.background_image]
        : []
  ).map(normalizeGoogleDriveImageUrl);

  const heroHasImage =
    (heroMode === 'slideshow' && heroSlideshowImages.length > 0) ||
    (heroMode === 'static' && Boolean(heroStaticImage));

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background based on mode */}
        {heroMode === 'slideshow' && heroSlideshowImages.length > 0 ? (
          <HeroSlideshow
            images={heroSlideshowImages}
            duration={heroContent.slideshow_duration || 5000}
          />
        ) : heroMode === 'static' && heroStaticImage ? (
          <div className="absolute inset-0 w-full h-full">
            <img
              src={heroStaticImage}
              alt="Hero background"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          </div>
        ) : (
          /* Default gradient background */
          <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-blue-50 to-green-50">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-20 left-10 w-64 h-64 bg-orange-400 rounded-full blur-3xl"></div>
              <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-300 rounded-full blur-3xl"></div>
            </div>
          </div>
        )}
        
        <div className="relative py-20 min-h-[600px] flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6 fade-in">
                <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight ${
                  (heroMode === 'slideshow' || heroMode === 'static') && heroHasImage
                    ? 'text-white drop-shadow-lg'
                    : 'text-gray-900'
                }`}>
                  {heroContent.title} <span className={
                    (heroMode === 'slideshow' || heroMode === 'static') && heroHasImage
                      ? 'text-orange-300'
                      : 'text-orange-500'
                  }>{heroContent.highlight}</span>
                </h1>
                <p className={`text-base leading-relaxed ${
                  (heroMode === 'slideshow' || heroMode === 'static') && heroHasImage
                    ? 'text-white drop-shadow-md'
                    : 'text-gray-700'
                }`}>
                  {heroContent.description}
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/membership"
                    data-testid="hero-join-btn"
                    className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-semibold hover:shadow-xl transition-all hover:scale-105"
                  >
                    {heroContent.button_primary}
                    <ArrowRight className="ml-2" size={20} />
                  </Link>
                  <Link
                    to="/about"
                    data-testid="hero-learn-more-btn"
                    className="inline-flex items-center justify-center px-8 py-4 bg-white text-orange-500 rounded-xl font-semibold border-2 border-orange-500 hover:bg-orange-50 transition-all"
                  >
                    {heroContent.button_secondary}
                  </Link>
                </div>
              </div>
              
              {/* Right side decorative element (hidden on mobile, only shown with gradient) */}
              {heroContent.image_mode === 'gradient' && (
                <div className="relative hidden md:block">
                  <div className="w-full h-96 bg-gradient-to-br from-orange-400 to-orange-600 rounded-3xl shadow-2xl transform rotate-3 hover:rotate-6 transition-transform duration-300"></div>
                  <div className="absolute inset-0 w-full h-96 bg-gradient-to-tl from-blue-300 to-blue-400 rounded-3xl shadow-2xl transform -rotate-3 hover:-rotate-6 transition-transform duration-300"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Thin divider between hero and activities */}
      {dividerContent.enabled && (
        <div
          style={{
            height: `${dividerContent.height_px || 2}px`,
            background: `linear-gradient(to right, ${dividerContent.color_1 || '#f97316'}, ${dividerContent.color_2 || '#60a5fa'}, ${dividerContent.color_3 || '#4ade80'})`,
          }}
        />
      )}



      {/* Association Activities Carousel (replaces stats section) */}
      <ActivitiesCarousel
        slides={activitiesContent.slides}
        intervalMs={activitiesContent.interval_ms || 4000}
      />

      {/* What We Stand For Section */}
      <section className="py-10 bg-gradient-to-br from-orange-50 via-blue-50 to-green-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{standForContent.heading}</h2>
            <MarkdownText
              markdown={standForContent.subheading}
              className="text-base text-gray-700 max-w-2xl mx-auto"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {(standForContent.cards || []).slice(0, 3).map((card, idx) => {
              const colorSchemes = {
                orange: {
                  bg: 'bg-gradient-to-br from-orange-500 to-orange-600',
                  overlay: 'from-orange-600/90 to-orange-700/90',
                  accent: 'bg-orange-400',
                  text: 'text-orange-100'
                },
                blue: {
                  bg: 'bg-gradient-to-br from-blue-500 to-blue-600',
                  overlay: 'from-blue-600/90 to-blue-700/90',
                  accent: 'bg-blue-400',
                  text: 'text-blue-100'
                },
                green: {
                  bg: 'bg-gradient-to-br from-green-500 to-green-600',
                  overlay: 'from-green-600/90 to-green-700/90',
                  accent: 'bg-green-400',
                  text: 'text-green-100'
                }
              };
              const defaultColors = ['orange', 'blue', 'green'];
              const colorKey = card.color || defaultColors[idx] || 'orange';
              const colors = colorSchemes[colorKey] || colorSchemes.orange;

              return (
                <div 
                  key={idx} 
                  className={`${colors.bg} rounded-xl overflow-hidden shadow-lg card-hover relative`}
                >
                  {/* Background Image */}
                  {card.image_url && (
                    <div className="absolute inset-0">
                      <img 
                        src={card.image_url} 
                        alt={card.title}
                        className="w-full h-full object-cover"
                      />
                      <div className={`absolute inset-0 bg-gradient-to-t ${colors.overlay}`} />
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="relative p-5 text-center min-h-[200px] flex flex-col justify-center">
                    <h3 className="text-lg font-bold text-white mb-2">{card.title}</h3>
                    <p className="text-white/90 text-xs leading-relaxed">{card.body_markdown}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>


      {/* Our Public Sector Coverage (Commitment Slider) */}
      <CommitmentSlider content={coverageContent} />

      {/* Latest News Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Latest News</h2>
              <p className="text-gray-600 mt-2">Stay updated with the latest developments</p>
            </div>
            <Link
              to="/news"
              data-testid="view-all-news-btn"
              className="hidden md:flex items-center text-orange-500 font-semibold hover:text-orange-600 transition-colors"
            >
              View All
              <ChevronRight size={20} />
            </Link>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : latestNews.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {latestNews.map((news) => (
                <Link
                  key={news.id}
                  to={`/news/${news.id}`}
                  data-testid={`news-card-${news.id}`}
                  className="bg-white rounded-2xl overflow-hidden shadow-md card-hover"
                >
                  <div className="h-48 bg-gradient-to-br from-orange-300 to-orange-500 flex items-center justify-center">
                    {news.image_url ? (
                      <img src={news.image_url} alt={news.title} className="w-full h-full object-cover" />
                    ) : (
                      <FileText className="text-white" size={48} />
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-3 py-1 bg-orange-100 text-orange-600 text-xs font-semibold rounded-full">
                        {news.category}
                      </span>
                      <span className="text-sm text-gray-500">
                        {new Date(news.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">{news.title}</h3>
                    <p className="text-gray-600 text-sm line-clamp-3">{news.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p>No news articles available yet.</p>
            </div>
          )}
          
          <div className="text-center mt-8 md:hidden">
            <Link
              to="/news"
              data-testid="mobile-view-all-news-btn"
              className="inline-flex items-center text-orange-500 font-semibold hover:text-orange-600 transition-colors"
            >
              View All News
              <ChevronRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Upcoming Events</h2>
              <p className="text-gray-600 mt-2">Join us at our upcoming events</p>
            </div>
            <Link
              to="/events"
              data-testid="view-all-events-btn"
              className="hidden md:flex items-center text-orange-500 font-semibold hover:text-orange-600 transition-colors"
            >
              View All
              <ChevronRight size={20} />
            </Link>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : upcomingEvents.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {upcomingEvents.map((event) => (
                <div
                  key={event.id}
                  data-testid={`event-card-${event.id}`}
                  className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 card-hover"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-16 h-16 bg-blue-400 rounded-xl flex flex-col items-center justify-center text-white shadow-lg">
                      <span className="text-2xl font-bold">{new Date(event.date).getDate()}</span>
                      <span className="text-xs uppercase">
                        {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{event.title}</h3>
                      <p className="text-sm text-gray-700 mb-2 line-clamp-2">{event.description}</p>
                      <div className="flex items-center text-sm text-gray-600 gap-4">
                        <span>{event.time}</span>
                        <span>•</span>
                        <span className="line-clamp-1">{event.location}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p>No upcoming events at the moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* World Labour News Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Globe className="mx-auto text-blue-500 mb-4" size={48} />
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{worldLabourNews.heading || 'World Labour News'}</h2>
            <p className="text-gray-600">{worldLabourNews.subheading || 'Stay informed about international labour developments and workers\' rights movements'}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {(worldLabourNews.categories || []).map((category, catIdx) => {
              const colorMap = {
                blue: { bg: 'from-blue-400 to-blue-600', text: 'text-blue-600', hover: 'hover:text-blue-700' },
                green: { bg: 'from-green-400 to-green-600', text: 'text-green-600', hover: 'hover:text-green-700' },
                orange: { bg: 'from-orange-400 to-orange-600', text: 'text-orange-600', hover: 'hover:text-orange-700' }
              };
              const colors = colorMap[category.color] || colorMap.blue;
              const latestItem = (category.items || [])[0];
              
              if (!latestItem) return null;
              
              return (
                <div key={category.id || catIdx} className="bg-white rounded-2xl p-6 shadow-md card-hover">
                  <div className="h-40 rounded-xl mb-4 overflow-hidden relative">
                    {latestItem.image_url ? (
                      <img 
                        src={latestItem.image_url} 
                        alt={latestItem.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className={`w-full h-full bg-gradient-to-br ${colors.bg} flex items-center justify-center`}>
                        <Globe className="text-white" size={48} />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  </div>
                  <span className={`text-xs ${colors.text} font-semibold uppercase tracking-wide`}>{category.name}</span>
                  <h3 className="text-lg font-bold text-gray-900 mt-2 mb-2 line-clamp-2">{latestItem.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-3">{latestItem.description}</p>
                  <a 
                    href={latestItem.link || '#'} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${colors.text} ${colors.hover} text-sm font-semibold mt-3 inline-flex items-center gap-1`}
                  >
                    Read More <ChevronRight size={16} />
                  </a>
                </div>
              );
            })}
            
            {/* Fallback if no categories */}
            {(!worldLabourNews.categories || worldLabourNews.categories.length === 0) && (
              <>
                <div className="bg-white rounded-2xl p-6 shadow-md card-hover">
                  <div className="h-40 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl mb-4 flex items-center justify-center">
                    <Globe className="text-white" size={48} />
                  </div>
                  <span className="text-xs text-blue-600 font-semibold">GLOBAL LABOUR</span>
                  <h3 className="text-lg font-bold text-gray-900 mt-2 mb-2">International Workers&apos; Rights Updates</h3>
                  <p className="text-sm text-gray-600">Latest developments in global labour standards and workplace protections...</p>
                  <a href="#" className="text-blue-500 text-sm font-semibold mt-3 inline-block hover:text-blue-600">
                    Read More →
                  </a>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-md card-hover">
                  <div className="h-40 bg-gradient-to-br from-green-400 to-green-600 rounded-xl mb-4 flex items-center justify-center">
                    <TrendingUp className="text-white" size={48} />
                  </div>
                  <span className="text-xs text-green-600 font-semibold">PACIFIC REGION</span>
                  <h3 className="text-lg font-bold text-gray-900 mt-2 mb-2">Pacific Labour Standards Progress</h3>
                  <p className="text-sm text-gray-600">Advances in workers&apos; conditions across Pacific Island nations...</p>
                  <a href="#" className="text-green-500 text-sm font-semibold mt-3 inline-block hover:text-green-600">
                    Read More →
                  </a>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-md card-hover">
                  <div className="h-40 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl mb-4 flex items-center justify-center">
                    <Shield className="text-white" size={48} />
                  </div>
                  <span className="text-xs text-orange-600 font-semibold">UNION MOVEMENTS</span>
                  <h3 className="text-lg font-bold text-gray-900 mt-2 mb-2">Trade Union Solidarity Actions</h3>
                  <p className="text-sm text-gray-600">Collective actions and achievements by unions worldwide...</p>
                  <a href="#" className="text-orange-500 text-sm font-semibold mt-3 inline-block hover:text-orange-600">
                    Read More →
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Publications Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <BookOpen className="mx-auto text-orange-500 mb-4" size={48} />
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Publications</h2>
            <p className="text-gray-600">Access our latest reports, guides, and informational materials</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Sample publications */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-6 card-hover text-center">
              <div className="w-20 h-28 bg-orange-500 rounded-lg mx-auto mb-4 shadow-lg flex items-center justify-center">
                <BookOpen className="text-white" size={32} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Annual Report 2024</h3>
              <p className="text-xs text-gray-600 mb-3">Comprehensive review of our activities and achievements</p>
              <button className="text-orange-600 font-semibold text-sm hover:text-orange-700">
                Download PDF
              </button>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 card-hover text-center">
              <div className="w-20 h-28 bg-blue-500 rounded-lg mx-auto mb-4 shadow-lg flex items-center justify-center">
                <FileText className="text-white" size={32} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Members&apos; Handbook</h3>
              <p className="text-xs text-gray-600 mb-3">Essential guide for all PEA PNG members</p>
              <button className="text-blue-600 font-semibold text-sm hover:text-blue-700">
                Download PDF
              </button>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 card-hover text-center">
              <div className="w-20 h-28 bg-green-500 rounded-lg mx-auto mb-4 shadow-lg flex items-center justify-center">
                <Shield className="text-white" size={32} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Rights & Entitlements</h3>
              <p className="text-xs text-gray-600 mb-3">Know your workplace rights and benefits</p>
              <button className="text-green-600 font-semibold text-sm hover:text-green-700">
                Download PDF
              </button>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 card-hover text-center">
              <div className="w-20 h-28 bg-purple-500 rounded-lg mx-auto mb-4 shadow-lg flex items-center justify-center">
                <TrendingUp className="text-white" size={32} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Career Development Guide</h3>
              <p className="text-xs text-gray-600 mb-3">Resources for professional growth</p>
              <button className="text-purple-600 font-semibold text-sm hover:text-purple-700">
                Download PDF
              </button>
            </div>
          </div>

          <div className="text-center mt-8">
            <Link
              to="/resources"
              className="inline-flex items-center text-orange-500 font-semibold hover:text-orange-600 transition-colors"
            >
              View All Resources
              <ChevronRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">{ctaContent.heading}</h2>
          <MarkdownText markdown={ctaContent.body_markdown} className="text-lg text-orange-100 mb-8" />
          <Link
            to={ctaContent.button_link || '/membership'}
            data-testid="cta-join-btn"
            className="inline-flex items-center px-8 py-4 bg-white text-orange-600 rounded-xl font-bold hover:bg-orange-50 transition-all hover:scale-105 shadow-xl"
          >
            {ctaContent.button_text || 'Join Now'}
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;